// Admin app entry.
//
// Flow:
//   1. Load /config.json.
//   2. If no Cognito session → show login. Otherwise → library.
//   3. Library: upload decks (S3 + manifest), open session view per deck.
//   4. Session view: shows speaker + viewer URLs, live presence list via IoT.

import { loadConfig }      from '../shared/config.js';
import { EventBus }        from '../shared/event-bus.js';
import { IotClient }       from '../shared/iot-client.js';
import { getAuthCredentials } from '../shared/credentials.js';
import {
  initAuth, existingSession, signIn, signOut, completeNewPassword,
} from './auth.js';
import { initStorage, loadManifest, uploadDeck, deleteDeck, createSession, endSession } from './storage.js';

const shell = document.getElementById('admin-shell');
const setView = (v) => shell.dataset.view = v;

let config       = null;
let idToken      = null;
let username     = null;
let presenceIot  = null;

// ── Login / new-password handling ────────────────────────────────────────
function bindLogin() {
  document.getElementById('login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const errEl = e.target.querySelector('[data-role="login-error"]');
    errEl.textContent = '';
    try {
      const r = await signIn(fd.get('username'), fd.get('password'));
      if (r.kind === 'new-password-required') {
        setView('new-password');
        bindNewPassword(r.user);
      } else {
        await onAuthenticated(r.idToken, r.username);
      }
    } catch (err) {
      errEl.textContent = err.message || String(err);
    }
  });
}

function bindNewPassword(user) {
  const form = document.getElementById('new-password-form');
  // Avoid stacking listeners across logins.
  const fresh = form.cloneNode(true);
  form.parentNode.replaceChild(fresh, form);
  fresh.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const errEl = e.target.querySelector('[data-role="np-error"]');
    if (fd.get('password') !== fd.get('confirm')) {
      errEl.textContent = 'Passwords do not match';
      return;
    }
    try {
      const r = await completeNewPassword(user, fd.get('password'));
      await onAuthenticated(r.idToken, r.username);
    } catch (err) {
      errEl.textContent = err.message || String(err);
    }
  });
}

async function onAuthenticated(token, name) {
  idToken  = token;
  username = name;

  // If we were sent here from /present/ (or anywhere else), bounce back.
  const ret = new URLSearchParams(location.search).get('return');
  if (ret) {
    // Only allow same-origin paths.
    if (ret.startsWith('/') && !ret.startsWith('//')) {
      location.replace(ret);
      return;
    }
  }

  const creds = await getAuthCredentials({
    region:         config.region,
    identityPoolId: config.identityPoolId,
    userPoolId:     config.userPoolId,
    idToken:        token,
  });
  initStorage({ region: config.region, decksBucket: config.decksBucket, creds });
  await showLibrary();
}

// ── Library view ─────────────────────────────────────────────────────────
async function showLibrary() {
  setView('library');
  document.querySelector('[data-role="username"]').textContent = username;

  const m = await loadManifest();
  renderDecks(m.decks || []);
  bindUpload();
  bindLibraryActions();
}

function renderDecks(decks) {
  const tbody = document.querySelector('[data-role="decks-tbody"]');
  if (!decks.length) {
    tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;color:var(--fg-muted);padding:32px">No decks yet — drop one above to get started.</td></tr>';
    return;
  }
  tbody.innerHTML = decks.map(d => `
    <tr data-deck="${d.id}">
      <td><code>${d.id}</code></td>
      <td>${escape(d.title || d.id)}</td>
      <td>${new Date(d.uploadedAt).toLocaleString()}</td>
      <td>${(d.size / 1024 / 1024).toFixed(1)} MB</td>
      <td class="actions">
        <button data-cmd="open">Start session</button>
        <button data-cmd="delete" class="ghost">Delete</button>
      </td>
    </tr>
  `).join('');
}

function bindUpload() {
  const zone     = document.getElementById('upload-zone');
  const input    = document.getElementById('file-input');
  const form     = zone.querySelector('[data-role="form"]');
  const filename = zone.querySelector('[data-role="filename"]');
  const filesize = zone.querySelector('[data-role="filesize"]');
  const deckIdEl = form.querySelector('input[name="deckId"]');
  const titleEl  = form.querySelector('input[name="title"]');
  const cancelEl = form.querySelector('[data-role="cancel"]');
  const progress = zone.querySelector('[data-role="upload-progress"]');

  let pendingFile = null;

  const reset = () => {
    pendingFile = null;
    zone.dataset.state = 'idle';
    form.reset();
    progress.textContent = '';
    progress.dataset.state = '';
    input.value = '';
  };

  const setProgress = (html, state = '') => {
    progress.innerHTML = html;
    progress.dataset.state = state;
  };

  const slugify = (name) => name.replace(/\.html$/i, '')
    .toLowerCase().replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '').slice(0, 40);

  const fmtSize = (n) => `${(n / 1024 / 1024).toFixed(1)} MB`;

  const stage = (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.html')) {
      setProgress('Only <code>.html</code> files (built reveal.js decks) are supported.', 'error');
      return;
    }
    pendingFile = file;
    filename.textContent = file.name;
    filesize.textContent = fmtSize(file.size);
    deckIdEl.value = slugify(file.name);
    titleEl.value  = slugify(file.name).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    zone.dataset.state = 'ready';
    setProgress('');
    deckIdEl.focus();
    deckIdEl.select();
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (!pendingFile) return;
    const deckId = deckIdEl.value.trim();
    const title  = titleEl.value.trim() || deckId;
    if (!/^[a-z0-9-]{2,40}$/.test(deckId)) {
      setProgress('Deck ID must be 2–40 chars: lowercase letters, digits, hyphens.', 'error');
      return;
    }

    zone.dataset.state = 'uploading';
    const total = pendingFile.size;
    setProgress(`Uploading ${pendingFile.name} <span class="bar"><span style="width:0%"></span></span>`);
    const bar = progress.querySelector('.bar > span');
    try {
      await uploadDeck({ deckId, title, htmlBlob: pendingFile, onProgress: (p) => {
        const pct = Math.round((p.loaded / total) * 100);
        if (bar) bar.style.width = pct + '%';
      }});
      setProgress(`Uploaded "${title}" successfully.`, 'ok');
      const m = await loadManifest();
      renderDecks(m.decks || []);
      setTimeout(reset, 1500);
    } catch (err) {
      setProgress('Upload failed: ' + (err.message || err), 'error');
      zone.dataset.state = 'ready';
    }
  });

  cancelEl.addEventListener('click', reset);

  ['dragenter', 'dragover'].forEach(evt =>
    zone.addEventListener(evt, (e) => { e.preventDefault(); zone.classList.add('dragover'); }));
  ['dragleave', 'drop'].forEach(evt =>
    zone.addEventListener(evt, () => zone.classList.remove('dragover')));
  zone.addEventListener('drop', (e) => { e.preventDefault(); stage(e.dataTransfer.files[0]); });
  input.addEventListener('change', () => stage(input.files[0]));
}

function bindLibraryActions() {
  document.querySelector('[data-role="decks-tbody"]').addEventListener('click', async (e) => {
    const btn = e.target.closest('button');
    if (!btn) return;
    const tr  = btn.closest('tr');
    const id  = tr?.dataset.deck;
    if (!id) return;
    if (btn.dataset.cmd === 'open')   showSession(id);
    if (btn.dataset.cmd === 'delete') {
      if (confirm(`Delete deck "${id}"? This is permanent.`)) {
        await deleteDeck(id);
        const m = await loadManifest();
        renderDecks(m.decks || []);
      }
    }
  });
  document.querySelector('button[data-cmd="logout"]').addEventListener('click', () => {
    signOut();
    location.reload();
  });
}

// ── Session view ─────────────────────────────────────────────────────────
async function showSession(deckId) {
  setView('loading');
  let session;
  try { session = await createSession(deckId); }
  catch (e) {
    alert('Could not start session: ' + (e.message || e));
    showLibrary();
    return;
  }
  setView('session');

  document.querySelector('[data-role="session-title"]').textContent = `${deckId} · session ${session}`;

  const viewerUrl  = `${location.origin}/${session}`;
  const speakerUrl = `${location.origin}/present/?deck=${encodeURIComponent(deckId)}&session=${session}`;
  document.querySelector('[data-role="speaker-url"]').textContent = speakerUrl;
  document.querySelector('[data-role="viewer-url"]').textContent  = viewerUrl;

  document.querySelector('button[data-cmd="open-speaker"]').onclick = () => window.open(speakerUrl, '_blank', 'noopener');
  document.querySelector('button[data-cmd="copy-speaker"]').onclick = () => navigator.clipboard.writeText(speakerUrl);
  document.querySelector('button[data-cmd="copy-viewer"]').onclick  = () => navigator.clipboard.writeText(viewerUrl);
  document.querySelector('button[data-cmd="back"]').onclick = async () => {
    if (presenceIot) { presenceIot.disconnect(); presenceIot = null; }
    try { await endSession(session); } catch {}
    showLibrary();
  };

  // Live presence via IoT.
  if (presenceIot) presenceIot.disconnect();
  const bus = new EventBus();
  presenceIot = new IotClient({ config, session, from: 'admin', bus, idToken });
  await presenceIot.connect();
  presenceIot.subscribe(`sessions/${session}/presence`);

  const presenceList = document.querySelector('[data-role="presence-list"]');
  const seen = new Map(); // clientId -> { from, lastSeen }
  const render = () => {
    const cutoff = Date.now() - 12_000;
    const live = [...seen.entries()].filter(([, v]) => v.lastSeen >= cutoff);
    if (!live.length) {
      presenceList.innerHTML = '<li class="empty">Waiting for clients to connect…</li>';
      return;
    }
    presenceList.innerHTML = live.map(([cid, v]) =>
      `<li><span class="dot"></span><strong>${escape(v.from)}</strong> ${escape(cid)}</li>`).join('');
  };
  bus.on('*', (payload, type) => {
    if (!type.startsWith('presence.')) return;
    if (payload.state === 'offline') seen.delete(payload.clientId);
    else seen.set(payload.clientId, { from: payload._meta?.from || '?', lastSeen: Date.now() });
    render();
  });
  setInterval(render, 2_000);
}

// ── Util ────────────────────────────────────────────────────────────────
function escape(s) {
  return String(s).replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// ── Boot ─────────────────────────────────────────────────────────────────
(async () => {
  config = await loadConfig();
  initAuth(config);

  const existing = await existingSession();
  if (existing) {
    await onAuthenticated(existing.idToken, existing.username);
  } else {
    setView('login');
    bindLogin();
  }
})();
